import {
  BANANA_PROGRESS_GAUGE_VIEWBOX_DEFAULT,
  clamp,
  first_defined,
  format_svg_number,
  gauge_clip_viewbox_from_data,
  normalise_viewbox,
  number_or_default,
} from "./banana_progress_gauge_core_base.js";

export function banana_progress_clip_points (pProgress, pBoundaryMode = "trace", pOptions = {})
  {
    const tOptions = (pOptions && typeof pOptions === "object") ? pOptions : {};
    return trace_clip_polygon_points (
      clamp (number_or_default (pProgress, 0), 0, 1),
      tOptions.tracePoints,
      tOptions.viewBox
    );
  }

export function boundary_options_from_data (pData)
  {
    return {
      tracePoints  : parse_trace_points (first_defined (
        pData,
        ["tracePoints", "pointsTrace", "pathPoints", "bezierPoints"]
      )) || (pData && pData.config && pData.config.tracePoints),
      viewBox : gauge_clip_viewbox_from_data (pData),
    };
  }

function parse_trace_points (pValue)
  {
    if (Array.isArray (pValue)) return pValue;
    if (typeof pValue !== "string" || !pValue.trim ()) return null;
    try
      {
        const tParsed = JSON.parse (pValue);
        return Array.isArray (tParsed) ? tParsed : null;
      }
    catch (e) { return null; }
  }

function default_trace_points ()
  {
    return [
      {x : 12, y : 22, inX : 2, inY : 22, outX : 45, outY : 48},
      {x : 128, y : 20, inX : 82, inY : 58, outX : 136, outY : 20},
    ];
  }

export function normalise_trace_points (pPoints)
  {
    const tSource = Array.isArray (pPoints) && pPoints.length >= 2 ? pPoints : default_trace_points ();
    return tSource
      .map ((pPoint) => {
        const tX = number_or_default (pPoint.x, 0);
        const tY = number_or_default (pPoint.y, 0);
        return {
          x    : tX,
          y    : tY,
          inX  : number_or_default (pPoint.inX, tX - 18),
          inY  : number_or_default (pPoint.inY, tY),
          outX : number_or_default (pPoint.outX, tX + 18),
          outY : number_or_default (pPoint.outY, tY),
        };
      })
      .sort ((pA, pB) => pA.x - pB.x);
  }

function trace_clip_polygon_points (pProgress, pTracePoints, pViewBox = BANANA_PROGRESS_GAUGE_VIEWBOX_DEFAULT)
  {
    const tPoint = trace_point_at_progress (pProgress, pTracePoints);
    const tTangent = tPoint.tangent;
    const tViewBox = normalise_viewbox (pViewBox);
    const tRect = [
      {x : tViewBox.x, y : tViewBox.y},
      {x : tViewBox.x + tViewBox.w, y : tViewBox.y},
      {x : tViewBox.x + tViewBox.w, y : tViewBox.y + tViewBox.h},
      {x : tViewBox.x, y : tViewBox.y + tViewBox.h},
    ];
    const tPolygon = clip_polygon_with_trace_normal (tRect, tPoint, tTangent);
    return tPolygon.map ((pPoint) =>
      `${format_svg_number (pPoint.x)},${format_svg_number (pPoint.y)}`
    ).join (" ");
  }

function clip_polygon_with_trace_normal (pPolygon, pPoint, pTangent)
  {
    const tDistance = (pCandidate) =>
      (pCandidate.x - pPoint.x) * pTangent.x + (pCandidate.y - pPoint.y) * pTangent.y;
    const tInside = (pCandidate) => tDistance (pCandidate) <= 0.0001;
    const tIntersection = (pA, pB) =>
      {
        const tDA = tDistance (pA);
        const tDB = tDistance (pB);
        const tRatio = tDA / (tDA - tDB || 1);
        return {
          x : pA.x + (pB.x - pA.x) * tRatio,
          y : pA.y + (pB.y - pA.y) * tRatio,
        };
      };

    const tOutput = [];
    for (let i = 0; i < pPolygon.length; i += 1)
      {
        const tCurrent = pPolygon[i];
        const tPrevious = pPolygon[(i + pPolygon.length - 1) % pPolygon.length];
        const tCurrentInside = tInside (tCurrent);
        const tPreviousInside = tInside (tPrevious);

        if (tCurrentInside)
          {
            if (!tPreviousInside)
              tOutput.push (tIntersection (tPrevious, tCurrent));
            tOutput.push (tCurrent);
          }
        else if (tPreviousInside)
          tOutput.push (tIntersection (tPrevious, tCurrent));
      }
    return tOutput.length ? tOutput : [{x : 0, y : 0}];
  }

function trace_point_at_progress (pProgress, pTracePoints)
  {
    const tPoints = normalise_trace_points (pTracePoints);
    const tProgress = clamp (number_or_default (pProgress, 0), 0, 1);
    const tSegmentCount = tPoints.length - 1;
    const tRaw = tProgress * tSegmentCount;
    const tSegmentIndex = clamp (Math.floor (tRaw), 0, tSegmentCount - 1);
    const tLocalT = tSegmentIndex === tSegmentCount ? 1 : tRaw - tSegmentIndex;
    const tA = tPoints[tSegmentIndex];
    const tB = tPoints[tSegmentIndex + 1];
    return cubic_point_and_tangent (
      tA,
      {x : tA.outX, y : tA.outY},
      {x : tB.inX, y : tB.inY},
      tB,
      tLocalT
    );
  }

function cubic_point_and_tangent (p0, p1, p2, p3, pT)
  {
    const t = clamp (number_or_default (pT, 0), 0, 1);
    const mt = 1 - t;
    const tPoint = {
      x : mt * mt * mt * p0.x + 3 * mt * mt * t * p1.x + 3 * mt * t * t * p2.x + t * t * t * p3.x,
      y : mt * mt * mt * p0.y + 3 * mt * mt * t * p1.y + 3 * mt * t * t * p2.y + t * t * t * p3.y,
    };
    const tTangent = {
      x : 3 * mt * mt * (p1.x - p0.x) + 6 * mt * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x),
      y : 3 * mt * mt * (p1.y - p0.y) + 6 * mt * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y),
    };
    const tLength = Math.hypot (tTangent.x, tTangent.y) || 1;
    return {x : tPoint.x, y : tPoint.y, tangent : {x : tTangent.x / tLength, y : tTangent.y / tLength}};
  }

